/**
 * Stays at this path, and keeps the default export, because the shared Discover
 * screens render it: they import the app's connected news panel from here.
 */
import { Dispatch, FC, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Action } from 'redux';

import { News } from '../../../lib/models/news';
import { RootState } from '../../../lib/store';
import {
  getLatestNews as serviceGetLatestNews,
  getNewsForSymbol as serviceGetNewsForSymbol,
} from '../../../lib/store/news';
import { isLoggedIn as serviceIsLoggedIn } from '../../../lib/store/selectors';
import GatewayWebsocketState from '../../../lib/store-util/GatewayWebsocketState';
import { isEmpty } from '../../../lib/util/typeGuards';

import LatestNews from '../../../shared_components/LatestNews/LatestNews';

type OwnProps = {
  selectedSymbol?: string;
  smallTitle?: boolean;
  isExtended?: boolean;
};
type Props = OwnProps & ReduxProps;

const LatestNewsWrapper: FC<Props> = ({
  latestNews,
  newsForSymbols,
  isLoggedIn,
  isLoading,
  getLatestNews,
  getNewsForSymbol,
  selectedSymbol,
  smallTitle,
  isExtended,
}: Props): JSX.Element => {
  const { isConnected: isGWConnected } = GatewayWebsocketState.use();
  const [isMounted, setIsMounted] = useState(false);

  const newsItems: News[] = selectedSymbol ? newsForSymbols[selectedSymbol] : latestNews;
  // Before the gateway connects there is nothing to wait on yet, so an empty list is
  // still "loading" rather than "no news".
  const isInitialLoad = !isLoading && isEmpty(newsItems) && !isGWConnected;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !isMounted || !isGWConnected) {
      return;
    }

    if (selectedSymbol) {
      getNewsForSymbol(selectedSymbol);
      return;
    }
    getLatestNews();
  }, [selectedSymbol, isLoggedIn, isMounted, isGWConnected]);

  return (
    <LatestNews
      newsItems={newsItems}
      isLoggedIn={isLoggedIn}
      isLoading={isLoading || isInitialLoad}
      selectedSymbol={selectedSymbol}
      smallTitle={smallTitle}
      isExtended={isExtended}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  isLoggedIn: serviceIsLoggedIn(state),
  newsForSymbols: state.news.newsForSymbols,
  latestNews: state.news.latestNews,
  isLoading: state.news.isLoading,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getLatestNews: () => dispatch(serviceGetLatestNews()),
  getNewsForSymbol: (symbol: string) => dispatch(serviceGetNewsForSymbol(symbol)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(LatestNewsWrapper);
